import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Avatar, SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import type { GuardianEventRowProps, GuardianEventRowVariant } from './types';

type StandardEventRowProps = Omit<
  GuardianEventRowProps,
  'bookingLabel' | 'isBookedByCurrentGuardian' | 'timeLabel' | 'variant'
> & {
  variant?: Exclude<GuardianEventRowVariant, 'dayPanel'>;
};

export default function StandardEventRow({
  accessibilityLabel,
  accentColor,
  chevronName = 'IcChevronRight',
  date,
  onPress,
  playerAvatar,
  playerName,
  showDivider = false,
  title,
  variant = 'card',
}: StandardEventRowProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const isCard = variant === 'card';
  const isList = variant === 'list';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? `${title} · ${playerName}`}
      onPress={onPress}
      style={({ pressed }) => [
        styles.root,
        isCard ? styles.cardRoot : null,
        variant === 'agenda' ? styles.agendaRoot : null,
        isList ? styles.listRoot : null,
        showDivider ? styles.divider : null,
        pressed ? styles.pressed : null,
      ]}>
      <Stack width={scale(4)} height={31} borderRadius={scale(999)} backgroundColor={accentColor} />

      {date ? (
        <Stack align="center" marginX={scale(8)}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 11 Regular" style={styles.day}>
            {date.day}
          </Text>
          <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.date}>
            {date.date}
          </Text>
        </Stack>
      ) : null}

      <Stack flex={1} marginLeft={!date && !isList ? scale(8) : undefined}>
        <Text variant="Font Guardian App/Body/Body 16 Semibold" style={styles.title}>
          {title}
        </Text>
        <Stack direction="row" align="center" gap={scale(4)}>
          <Avatar size={scale(20)} borderWidth={0} activeOpacity={1}>
            <Avatar.Image source={playerAvatar} backgroundColor={guardianColors['2']} />
          </Avatar>
          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.playerName}>
            {playerName}
          </Text>
        </Stack>
      </Stack>

      <SvgIcon name={chevronName} size={scale(20)} color={guardianColors['5']} />
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors['0'],
  },
  cardRoot: {
    borderRadius: scale(12),
    paddingHorizontal: scale(12),
    paddingVertical: scale(12),
    shadowColor: '#0F225B',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  agendaRoot: {
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
  },
  listRoot: {
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
    gap: scale(8),
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: guardianColors['1'],
  },
  pressed: {
    backgroundColor: guardianColors['2'],
  },
  day: {
    color: guardianColors['6'],
    textAlign: 'center',
  },
  date: {
    color: guardianColors['10'],
  },
  title: {
    color: guardianColors['10'],
  },
  playerName: {
    color: guardianColors['6'],
  },
}));
